import {first} from 'lodash';
import {ellipsis, transparentize} from 'polished';
import React, {FC, ReactNode, useCallback, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {
  KeyboardActionsEnum,
  noShortcutsMapping,
} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {isInteger} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {intersperse} from '../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {AttachmentModel} from '../../../../libs/shared/core/src/models/api/attachmentModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateShortDisplayName} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {NewPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TeammateAvatar} from '../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {RelativeDateTimeFormatsEnum} from '../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {Dropdown, DropdownProps} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {GridAreaItem} from '../../../../libs/shared/ui/src/components/grid/grid';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {
  keyRenderers,
  splitAndFixKeys,
} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcutFormatterHelpers';
import {useLinkNavigation} from '../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {commentOnlyHasAnnotation} from '../../../helpers/models/commentHelpers';
import {CommentModel} from '../../../models/api/commentModel';
import {CommentUnfurlTypesEnum, TopicCommentUnfurlModel} from '../../../models/api/commentUnfurlModel';
import {OnboardingTipTypesEnum} from '../../../models/api/preferencesModel';
import {TopicModel} from '../../../models/api/topicModel';
import {getSeenOnboardingTips} from '../../../store/data/preferences/preferencesSelectors';
import {CommentBody} from '../../inboxes/viewer/activity/comment/commentBody';
import {AnnotationTopicReferencePreviewerUnfurlContainer} from '../../inboxes/viewer/activity/comment/unfurl/referencePreviewer/topicReferencePreviewerUnfurlContainer';
import {InteractiveDateTimeFormatter} from '../formatters/dateTime/interactiveDateTimeFormatter';
import {useInfoBubblesContext} from '../infoBubbles/infoBubblesContext';

export const floatingActivityDropdownId = 'floating-activity-dropdown';

interface FloatingActivityDropdownProps extends DropdownProps {
  panelId: NewPanelId;
  comment: CommentModel | null;
  topic?: TopicModel;
  activeUrl?: string;
  userId: number;
  author?: TeammateModel;
  attachments: ReadonlyArray<AttachmentModel>;
  mentionableAliases: ReadonlySet<string>;
  userAlias: string;
  onJumpCommentClick: () => void;
  conversationId?: number;
  namespace?: string;
}

/*
 * Styles.
 */

const StyledDropdown = styled(Dropdown)<{$hasHover?: boolean}>`
  min-width: 300px;
  border-radius: 12px;
  padding-top: 10px;
  ${(p) =>
    p.$hasHover &&
    css`
      &:hover {
        background-color: ${p.theme.greys.shade10};
      }
    `};
`;

const StyledHeaderContentDiv = styled.div`
  display: grid;
  grid-template-areas: 'avatar . text . timestamp';
  grid-template-columns: auto 6px 1fr 16px auto;
  align-items: center;
  position: relative;

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
`;

const StyledHeaderHoverLabel = styled.div`
  z-index: 1;
  opacity: 0;
  transition: opacity 300ms ease;
  color: ${(p) => p.theme.interactiveText};
  background-color: ${(p) => p.theme.greys.shade10};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.verySmall};
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;

  ${StyledHeaderContentDiv}:hover & {
    opacity: 1;
  }
`;

const StyledAuthorDiv = styled.div`
  grid-area: text;
  margin-top: -1px;
  ${ellipsis()}
`;

const StyledTimestampDiv = styled.div`
  grid-area: timestamp;
  display: flex;
  justify-content: flex-end;
  text-align: right;

  color: ${(p) => p.theme.greys.shade60};
  text-transform: uppercase;

  ${ellipsis()}
`;

const StyledCommentBody = styled.div`
  background-color: ${(p) => p.theme.greys.shade30};
  border-radius: 7px;
  margin-top: 8px;
  position: relative;

  overflow: hidden;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

const StyledCommentBodyHover = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: center;
  background: linear-gradient(
    180deg,
    ${(p) => transparentize(0.4, p.theme.greys.shade30)} 0%,
    ${(p) => p.theme.greys.shade30} 100%
  );
  z-index: 1;
  opacity: 0;
  transition: opacity 300ms ease;

  ${StyledCommentBody}:hover & {
    opacity: 1;
  }
`;

const StyledCommentBodyHoverLabel = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  color: ${(p) => p.theme.interactiveText};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.verySmall};
`;

const StyledAnnotationTopicViewerReferencePreviewerUnfurlContainer = styled(
  AnnotationTopicReferencePreviewerUnfurlContainer,
)`
  margin: 0;
`;

interface StyledCommentDivProps {
  $shouldShowBorder: boolean;
  $shouldHavePaddingTop: boolean;
}

const StyledCommentDiv = styled.div<StyledCommentDivProps>`
  padding: ${(p) => (p.$shouldHavePaddingTop ? `8px 16px 10px 16px` : `0 16px 10px 16px`)};
  border-top: ${(p) => (p.$shouldShowBorder ? `1px solid ${p.theme.alphas.black30}` : ``)};
`;

const StyledActiveUrlDiv = styled.div`
  display: flex;

  width: 100%;
  border-bottom: 1px solid ${(p) => p.theme.alphas.black30};

  gap: 4px;
  line-height: 20px;
  padding: 0 16px 8px 16px;

  color: ${(p) => p.theme.palette.blue.shade40};

  &:hover {
    color: ${(p) => p.theme.palette.blue.shade50};
  }
`;

export const StyledHeaderLinkDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  overflow: hidden;
  font-weight: ${fontWeights.semibold};
`;

const StyledMultiSelectEducationCard = styled.div`
  display: grid;
  grid-template-areas: 'icon text button';
  column-gap: 11px;

  padding: 10px;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;

  background-color: ${(p) => p.theme.palette.pink.shade20};
`;

const StyledEducationIconDiv = styled.div`
  grid-area: icon;
`;

const StyledEducationIcon = styled(Icon)`
  color: ${(p) => p.theme.palette.pink.shade40};
`;

const StyledEducationTextDiv = styled.div`
  grid-area: text;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  color: ${(p) => p.theme.greys.shade80};

  line-height: 17px;
`;

const StyledEducationButtonDiv = styled.div`
  grid-area: button;
  gap: 4px;

  height: 26px;
  width: 26px;
  border-radius: 6px;
  margin-top: -5px;

  color: ${(p) => p.theme.greys.shade80};

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledIconDiv = styled.div`
  padding: 5px;
`;

const StyledKeySpan = styled.span`
  justify-content: center;
  align-items: center;

  border-radius: 4px;
  height: 16px;
  padding: 1px 4px;

  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade80};
  background-color: ${(p) => p.theme.alphas.black20};
  text-transform: capitalize;
`;

/*
 * Component.
 */

export const FloatingActivityDropdown: FC<FloatingActivityDropdownProps> = (props) => {
  const {
    comment,
    topic,
    activeUrl,
    author,
    onRequestClose,
    anchor,
    userId,
    namespace,
    conversationId,
    panelId,
  } = props;

  // We are using an info bubbles preference to keep track of whether the user has seen a multiselect education card before.
  const context = useInfoBubblesContext();
  const seenOnboardingTips = useAppSelector(getSeenOnboardingTips);
  const shouldRenderMultiselectEducationCard =
    !seenOnboardingTips.includes(OnboardingTipTypesEnum.MULTISELECT_TOPIC_ANNOTATION) && topic;
  const onMultiselectEducationCardClose = () =>
    context.closeInfoBubbles(OnboardingTipTypesEnum.MULTISELECT_TOPIC_ANNOTATION);

  const isHighlight = Boolean(comment && commentOnlyHasAnnotation(comment));
  const shouldTrackActions = useMemo(
    () => isInteger(conversationId) && namespace,
    [conversationId, namespace],
  );
  const maybeTrackJumpToComment = useCallback(() => {
    if (!shouldTrackActions) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.P3_MESSAGE_COMMENT_CLICK, {
      namespace,
      conversation_id: conversationId,
      cta_type: 'jump_to_comment',
      location: 'p3_window',
      has_comment_preview: !isHighlight,
    });
  }, [conversationId, isHighlight, namespace, shouldTrackActions]);

  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.P3_MESSAGE_COMMENT_PREVIEW, {
      namespace,
      conversation_id: conversationId,
      has_comment_preview: !isHighlight,
    });
  });

  const {navigateToLink} = useLinkNavigation();

  if (!topic && !comment) {
    return null;
  }

  const isUserTheAuthor = author?.id === userId;
  const hasAnnotation = Boolean(comment?.annotationUid);

  const onHeaderClick = isHighlight
    ? () => {
        props.onJumpCommentClick();
        props.onRequestClose?.();
        maybeTrackJumpToComment();
      }
    : undefined;

  const unfurl: TopicCommentUnfurlModel = {
    type: CommentUnfurlTypesEnum.TOPIC,
    token: topic?.alias ?? '',
    topic,
    topicId: topic?.id,
  };

  return (
    <StyledDropdown
      width={300}
      maxHeight={500}
      anchor={anchor}
      placement={props.placement}
      onRequestClose={onRequestClose}
      $hasHover={isHighlight}
      isExclusive={false}
      isInline
    >
      <div id={floatingActivityDropdownId}>
        {/* Active URL section. */}
        {activeUrl && (
          <StyledActiveUrlDiv onClick={() => navigateToLink(activeUrl)}>
            <Icon name="externalLink" />
            <StyledHeaderLinkDiv>
              <TooltipCoordinator render={() => activeUrl} placement="top">
                <TooltipOverflowCondition>{activeUrl}</TooltipOverflowCondition>
              </TooltipCoordinator>
            </StyledHeaderLinkDiv>
          </StyledActiveUrlDiv>
        )}
        {/* Topic annotation section. */}
        {topic && (
          <StyledAnnotationTopicViewerReferencePreviewerUnfurlContainer
            key={unfurl.token}
            unfurl={unfurl}
            isRenderingUrl={Boolean(activeUrl)}
            isRenderingComment={Boolean(comment)}
          />
        )}
        {/* Comment annotation section. */}
        {comment && author && (
          <StyledCommentDiv
            $shouldShowBorder={Boolean(topic)}
            $shouldHavePaddingTop={Boolean(topic) || Boolean(activeUrl)}
          >
            <StyledHeaderContentDiv onClick={onHeaderClick}>
              {isHighlight && (
                <StyledHeaderHoverLabel>
                  <Icon name="arrowThickSmall" />
                  <Trans id="floating-comment-jump">Jump</Trans>
                </StyledHeaderHoverLabel>
              )}
              <GridAreaItem area="avatar">
                <TeammateAvatar teammate={author} size={VisualSizesEnum.MEDIUM} />
              </GridAreaItem>
              <StyledAuthorDiv>{renderTitle(author, isUserTheAuthor, isHighlight)}</StyledAuthorDiv>
              <StyledTimestampDiv>
                <InteractiveDateTimeFormatter
                  relativeFormat={RelativeDateTimeFormatsEnum.SHORT}
                  value={comment.sentAt}
                />
              </StyledTimestampDiv>
            </StyledHeaderContentDiv>
            {!isHighlight && (
              <StyledCommentBody>
                {hasAnnotation && (
                  <StyledCommentBodyHover
                    onClick={() => {
                      props.onJumpCommentClick();
                      props.onRequestClose?.();
                      maybeTrackJumpToComment();
                    }}
                  >
                    <StyledCommentBodyHoverLabel>
                      <Icon name="arrowThickSmall" />
                      <Trans id="floating-comment-jumptocomment">Jump to comment</Trans>
                    </StyledCommentBodyHoverLabel>
                  </StyledCommentBodyHover>
                )}
                <CommentBody
                  comment={comment}
                  mentionableAliases={props.mentionableAliases}
                  userAlias={props.userAlias}
                  attachments={props.attachments}
                  panelId={panelId}
                  shouldHideAnnotation
                  namespace={undefined}
                />
              </StyledCommentBody>
            )}
          </StyledCommentDiv>
        )}
        {shouldRenderMultiselectEducationCard &&
          renderMultiselectEducationCard(onMultiselectEducationCardClose)}
      </div>
    </StyledDropdown>
  );
};

function renderMultiselectEducationCard(onClose: () => void) {
  const keyboardShortcut = noShortcutsMapping[KeyboardActionsEnum.OPEN_LINK];
  const sequence = first(keyboardShortcut)?.sequence;

  function renderKeyboardShortcutKeys(shortcut: string) {
    const fixedKeys = splitAndFixKeys(shortcut);

    const translatedKeys = fixedKeys.map((k) => {
      const renderer = keyRenderers[k];
      return (
        <StyledKeySpan key={k}>
          <span>{renderer ? renderer() : k}</span>
        </StyledKeySpan>
      );
    });

    return <>{intersperse<ReactNode>(translatedKeys, ' ')}</>;
  }

  return (
    <StyledMultiSelectEducationCard>
      <StyledEducationIconDiv>
        <StyledEducationIcon name="lightbulbSmall" />
      </StyledEducationIconDiv>
      <StyledEducationTextDiv>
        {sequence && renderKeyboardShortcutKeys(sequence)}{' '}
        <Trans id="floatingactivity-dropdown-educationcard">
          To multi-select objects for easy copy/pasting of particular ID numbers.
        </Trans>
      </StyledEducationTextDiv>
      <StyledEducationButtonDiv onClick={onClose}>
        <TooltipCoordinator
          render={() => <Trans id="floatingactivity-dropdown-educationcard-close">Close</Trans>}
        >
          <StyledIconDiv>
            <Icon name="closeSmall" />
          </StyledIconDiv>
        </TooltipCoordinator>
      </StyledEducationButtonDiv>
    </StyledMultiSelectEducationCard>
  );
}

function renderTitle(author: TeammateModel, isUserTheAuthor: boolean, isHighlight: boolean) {
  if (isHighlight) {
    if (isUserTheAuthor) {
      return (
        <Trans id="floating-comment-author-you">
          <strong>You</strong> added this in a comment
        </Trans>
      );
    }

    return (
      <Trans id="floating-comment-author-highlighted">
        <strong>{buildTeammateShortDisplayName(author)}</strong> added this in a comment
      </Trans>
    );
  }

  if (isUserTheAuthor) {
    return (
      <Trans id="floating-comment-author-you-commented">
        <strong>You</strong> commented
      </Trans>
    );
  }

  return (
    <Trans id="floating-comment-author-commented">
      <strong>{buildTeammateShortDisplayName(author)}</strong> commented
    </Trans>
  );
}
